
class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.init();
    document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    this.infiniteScroll = this.dataset.infiniteScroll === 'true';
    this.productGrid = this.querySelector('[id^="product-list-"]');
    this.loadMoreButton = this.querySelector('.js-pagination-load-more');
    this.loader = document.querySelector('.products-loader');
    this.isFetching = false;
    this.controller = null;

    if (!this.productGrid || !this.loadMoreButton) {
      console.warn('Infinite scroll: missing product grid or loadMoreButton.');
      return;
    }

    // Loading indicator

    if (this.loader) {
      this.loader.style.display = 'block';
    }

    // Bind load more click handler
    this.loadMoreClickHandler = this.handleLoadMoreClick.bind(this);
    if (!this.infiniteScroll) {
      this.loadMoreButton.style.cssText = 'display: block; margin: 2rem auto; text-align: center;';
      this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
    }

    // Initialize IntersectionObserver for infinite scroll
    if (this.infiniteScroll) {
      this.paginationObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isFetching) {
              this.loadMore();
              // Continuously load while visible
              this.intersectingTimer = setInterval(() => {
                if (!this.isFetching) this.loadMore();
              }, 1000);
            } else if (this.intersectingTimer) {
              clearInterval(this.intersectingTimer);
            }
          });
        },
        { rootMargin: `${window.innerHeight * 1.5}px 0px` }
      );
      this.paginationObserver.observe(this.loadMoreButton);
    }
  }

  destroy() {
    if (this.intersectingTimer) clearInterval(this.intersectingTimer);
    if (this.paginationObserver) this.paginationObserver.disconnect();
    if (this.loadMoreButton && this.loadMoreClickHandler) {
      this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
    }
    if (this.loader) {
      this.loader.style.display = 'none';
    }
    // if (this.loader) this.loader.remove();
  }

  reload() {
    this.destroy();
    this.init();
  }

  handleLoadMoreClick(evt) {
    evt.preventDefault();
    this.loadMore();
  }

  async loadMore() {
    if (this.isFetching) return;
    this.isFetching = true;
    this.controller = new AbortController();

    try {
      const url = this.loadMoreButton.getAttribute('href');
      if (!url) throw new Error('No URL found for load more.');

      // Add loading state
      this.loadMoreButton.classList.add('is-loading');
      this.loadMoreButton.setAttribute('aria-disabled', 'true');
      this.productGrid.insertAdjacentElement('afterend', this.loader);

      // Fetch next page
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      // Parse response
      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      const newProducts = tmpl.content.querySelectorAll('.js-pagination-result');

      // Append new products
      let newResultsHtml = '';
      newProducts.forEach((product) => {
        const productCards = product.querySelectorAll('product-card[reveal-on-scroll="true"]');
        productCards.forEach((card) => {
          card.removeAttribute('reveal-on-scroll');
          card.style.opacity = '1'; //Ensure product visibility
          card.style.transform = 'none'; //Remove transform
        });
        newResultsHtml += product.outerHTML;
      });
      this.productGrid.insertAdjacentHTML('beforeend', newResultsHtml);

      const newLoadMore = tmpl.content.querySelector('.js-pagination-load-more');
      if (newLoadMore) {
        this.loadMoreButton.setAttribute('href', newLoadMore.getAttribute('href'));
      } else {
        this.loadMoreButton.remove();
        if (this.paginationObserver) this.paginationObserver.disconnect();
        if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Infinite scroll error:', err);
      }
    } finally {
      this.isFetching = false;
      this.controller = null;

      // Remove loading state
      this.loadMoreButton.classList.remove('is-loading');
      this.loadMoreButton.removeAttribute('aria-disabled');
      if (this.loader) this.loader.remove();
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
